import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ApostilleDocumentType, ApostilleRequest, ApostilleSourceType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const DOC_TYPES: ApostilleDocumentType[] = [
  'BIRTH_CERTIFICATE',
  'DEATH_CERTIFICATE',
  'MARRIAGE_CERTIFICATE',
  'EDUCATIONAL_CERTIFICATE',
  'POLICE_CLEARANCE',
  'COURT_DOCUMENT',
  'COMMERCIAL_DOCUMENT',
  'POWER_OF_ATTORNEY',
  'OTHER',
];

/**
 * Apostille pipeline — document legalisation (spec: "ApostilleRequest —
 * document(DocVault or upload), verification vs issuing registry via Sey-X,
 * e-apostille with QR (e-APP standard), fee"). The genuinely public verify
 * page lives at /verify (see src/pages/VerifyApostille.tsx) — linked
 * prominently from here too since staff often need to hand that URL to an
 * applicant or foreign registry.
 */
export function ApostilleRequests() {
  const { persona } = useAuth();
  const isAuthority = persona?.roles.includes('APOSTILLE_AUTHORITY') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m42/apostille-requests?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<ApostilleRequest>>(path);

  return (
    <div>
      <PageHeader
        title="Apostille & Legalisation"
        subtitle="Document legalisation — registry verification, fee, then e-Apostille issuance with a QR-verifiable record."
        actions={
          <div className="flex items-center gap-2">
            <a href="/verify" target="_blank" rel="noreferrer" className="btn-secondary">
              Public verify page ↗
            </a>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New request
            </button>
          </div>
        }
      />

      {!isAuthority ? <RoleNotice roles={['APOSTILLE_AUTHORITY']} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-52" placeholder="Applicant ID, name or apostille no…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="REGISTRY_CHECKED">Registry checked</option>
            <option value="FEE_PENDING">Fee pending</option>
            <option value="AUTHORITY_REVIEW">Authority review</option>
            <option value="APOSTILLED">Apostilled</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No apostille requests match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Applicant</th>
                <th className="table-head">Document type</th>
                <th className="table-head">Destination</th>
                <th className="table-head">e-Apostille no.</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(r.id)}>
                  <td className="table-cell font-medium">{r.applicantName}</td>
                  <td className="table-cell text-xs">{r.documentType.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{r.destinationCountry}</td>
                  <td className="table-cell font-mono text-xs">{r.apostilleNo ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <RequestDetailModal
          id={selectedId}
          isAuthority={isAuthority}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateRequestModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function RequestDetailModal({
  id,
  isAuthority,
  onClose,
  onChanged,
}: {
  id: string;
  isAuthority: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<ApostilleRequest>(`/api/v1/m42/apostille-requests/${id}`);
  const r = detailQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={r ? `${r.applicantName} — ${r.documentType.replaceAll('_', ' ')}` : 'Apostille request'} onClose={onClose} wide>
      {detailQ.error ? <ErrorNote message={detailQ.error} /> : null}
      {detailQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {r ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={r.state} />} />
            <Detail label="Applicant type" value={r.applicantType} />
            <Detail label="Applicant ID" value={r.applicantId} mono />
            <Detail label="Contact email" value={r.contactEmail} />
            <Detail label="Destination country" value={r.destinationCountry} />
            <Detail label="Source" value={r.sourceType === 'DOCVAULT' ? `DocVault ${r.sourceDocVaultSerial}` : `Upload ${r.sourceDocRef}`} mono={r.sourceType === 'DOCVAULT'} />
            <Detail label="Fee" value={r.feePaidAt ? `${scr(r.feeAmount)} — paid ${formatDateTime(r.feePaidAt)}` : r.feeAmount ? `${scr(r.feeAmount)} due` : '—'} />
            {r.payIntentRefNo ? <Detail label="Payment reference" value={r.payIntentRefNo} mono /> : null}
          </div>

          {r.registryVerification ? (
            <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700">
              <span className="font-semibold">Registry check</span> ({r.registryVerification.method}):{' '}
              {r.registryVerification.checked ? (
                <StatusBadge status={r.registryVerification.valid ? 'VALID' : 'INVALID'} />
              ) : (
                <span className="text-slate-500">{r.registryVerification.reason ?? 'Not automated for this document type'}</span>
              )}
            </div>
          ) : null}

          {r.state === 'FEE_PENDING' ? (
            <div className="rounded-lg bg-violet-50 px-3.5 py-2.5 text-sm text-violet-800">
              Awaiting SeyPay confirmation for reference <span className="font-mono font-semibold">{r.payIntentRefNo}</span> — advances automatically
              once paid.
            </div>
          ) : null}

          {r.state === 'APOSTILLED' ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
              <div>
                e-Apostille issued — no. <span className="font-mono font-semibold">{r.apostilleNo}</span> on {formatDateTime(r.authorisedAt)}
              </div>
              {r.apostilleVerifyUrl ? (
                <a href={r.apostilleVerifyUrl} target="_blank" rel="noreferrer" className="underline">
                  Public verify link ↗
                </a>
              ) : null}
              <div className="mt-1">
                Or check it on this portal's own{' '}
                <a href={`/verify?ref=${encodeURIComponent(r.apostilleNo ?? '')}`} target="_blank" rel="noreferrer" className="underline">
                  public verify page ↗
                </a>
              </div>
            </div>
          ) : null}
          {r.state === 'REJECTED' && r.rejectionReason ? <ErrorNote message={`Rejected: ${r.rejectionReason}`} /> : null}

          {isAuthority ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="flex flex-wrap gap-2">
                {r.state === 'SUBMITTED' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/apostille-requests/${r.id}/verify-registry`))}>
                    {busy ? 'Checking…' : 'Verify against registry'}
                  </button>
                ) : null}
                {r.state === 'REGISTRY_CHECKED' ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/apostille-requests/${r.id}/fee`))}>
                    {busy ? 'Requesting…' : 'Request fee (SeyPay intent)'}
                  </button>
                ) : null}
                {r.state === 'AUTHORITY_REVIEW' ? (
                  <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/apostille-requests/${r.id}/authorise`))}>
                    {busy ? 'Issuing…' : 'Authorise — issue e-Apostille'}
                  </button>
                ) : null}
                {['SUBMITTED', 'REGISTRY_CHECKED', 'FEE_PENDING', 'AUTHORITY_REVIEW'].includes(r.state) ? (
                  <button className="btn-danger" disabled={busy} onClick={() => setShowReject((s) => !s)}>
                    Reject
                  </button>
                ) : null}
              </div>
              {showReject ? (
                <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <Field label="Rejection reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className="btn-danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/apostille-requests/${r.id}/reject`, { reason }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm reject'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={['APOSTILLE_AUTHORITY']} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Filed {formatDateTime(r.createdAt)} · last updated {formatDateTime(r.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateRequestModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [applicantType, setApplicantType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [applicantId, setApplicantId] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [sourceType, setSourceType] = useState<ApostilleSourceType>('DOCVAULT');
  const [sourceDocVaultSerial, setSourceDocVaultSerial] = useState('');
  const [sourceDocRef, setSourceDocRef] = useState('');
  const [sourceSha256, setSourceSha256] = useState('');
  const [documentType, setDocumentType] = useState<ApostilleDocumentType>('BIRTH_CERTIFICATE');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sourceValid =
    sourceType === 'DOCVAULT' ? sourceDocVaultSerial.trim().length > 0 : sourceDocRef.trim().length > 0 && /^[0-9a-f]{64}$/i.test(sourceSha256.trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m42/apostille-requests', {
        applicantType,
        applicantId: applicantId.trim(),
        applicantName,
        contactEmail,
        sourceType,
        sourceDocVaultSerial: sourceType === 'DOCVAULT' ? sourceDocVaultSerial.trim() : undefined,
        sourceDocRef: sourceType === 'UPLOAD' ? sourceDocRef.trim() : undefined,
        sourceSha256: sourceType === 'UPLOAD' ? sourceSha256.trim().toLowerCase() : undefined,
        documentType,
        destinationCountry,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit apostille request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New apostille request" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant type">
            <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="PERSON">Person (NIN)</option>
              <option value="ENTITY">Entity (business ID)</option>
            </select>
          </Field>
          <Field label={applicantType === 'PERSON' ? 'Applicant NIN' : 'Business ID'}>
            <input className="field-input font-mono" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant name">
            <input className="field-input" value={applicantName} onChange={(e) => setApplicantName(e.target.value)} />
          </Field>
          <Field label="Contact email">
            <input type="email" className="field-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Document type">
            <select className="field-input" value={documentType} onChange={(e) => setDocumentType(e.target.value as ApostilleDocumentType)}>
              {DOC_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Destination country">
            <input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value)} placeholder="Hague-convention state the document is bound for" />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <div className="mb-2 flex items-center gap-3 text-xs font-bold uppercase tracking-wide text-slate-500">
            <span>Source document</span>
            <label className="flex items-center gap-1 font-normal normal-case">
              <input type="radio" checked={sourceType === 'DOCVAULT'} onChange={() => setSourceType('DOCVAULT')} /> Already in DocVault
            </label>
            <label className="flex items-center gap-1 font-normal normal-case">
              <input type="radio" checked={sourceType === 'UPLOAD'} onChange={() => setSourceType('UPLOAD')} /> Uploaded scan
            </label>
          </div>
          {sourceType === 'DOCVAULT' ? (
            <Field label="DocVault serial">
              <input className="field-input font-mono" value={sourceDocVaultSerial} onChange={(e) => setSourceDocVaultSerial(e.target.value)} placeholder="e.g. BIRT-SEED-0001" />
            </Field>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-amber-700">
                No document-upload endpoint exists in this build — enter the object-storage reference &amp; SHA-256 hash the way an out-of-band
                upload step would produce (see the module's own convention: multipart → object storage → Document record with SHA-256).
              </p>
              <Field label="Document reference (object storage URI)">
                <input className="field-input font-mono text-xs" value={sourceDocRef} onChange={(e) => setSourceDocRef(e.target.value)} placeholder="objectstore://m42/uploads/…" />
              </Field>
              <Field label="SHA-256 (64 hex characters)">
                <input className="field-input font-mono text-xs" value={sourceSha256} onChange={(e) => setSourceSha256(e.target.value)} placeholder="64-character hex hash" />
              </Field>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || applicantId.trim().length < 3 || applicantName.trim().length < 2 || !contactEmail.includes('@') || destinationCountry.trim().length < 2 || !sourceValid}
          >
            {busy ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
